// Approve and Ignore, optimistically. The card resolves on screen at once (every list that shows it: Requests,
// the Assistant, Notifications, the badges), the request goes out, and if it fails everything is put back as it
// was and the reason is shown. Ignore offers Undo; an approval offers Open (what it did is real, so no undo).
import { useState } from 'react';
import { useMutation, useQueryClient, type InfiniteData, type QueryKey } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { isSuggestion, type ActionStatus, type ConversationDetail, type NotificationList, type ProposedAction, type Summary } from '@dpw/shared';
import { api } from '../api';
import { useToast } from '../ui/toast';

type Snapshot = [QueryKey, unknown][];

function applyStatus(qc: ReturnType<typeof useQueryClient>, a: Decidable, status: ActionStatus): Snapshot {
  const snap: Snapshot = [
    ...qc.getQueriesData({ queryKey: ['actions'] }), ...qc.getQueriesData({ queryKey: ['conversation'] }),
    ...qc.getQueriesData({ queryKey: ['notifications'] }), ...qc.getQueriesData({ queryKey: ['summary'] }),
  ];
  const now = new Date().toISOString();
  const set = (x: ProposedAction): ProposedAction => (x.id === a.id ? { ...x, status, decidedAt: now } as ProposedAction : x);
  qc.setQueriesData<ProposedAction[]>({ queryKey: ['actions'] }, list => list?.map(set));
  qc.setQueriesData<ConversationDetail>({ queryKey: ['conversation'] }, c => (c ? { ...c, actions: c.actions.map(set) } : c));
  qc.setQueriesData<InfiniteData<NotificationList>>({ queryKey: ['notifications'] }, d => d && {
    ...d, pages: d.pages.map(p => ({ ...p, items: p.items.map(n => (n.action?.id === a.id ? { ...n, action: { ...n.action, status } } : n)) })),
  });
  if (a.status === 'waiting') {
    qc.setQueriesData<Summary>({ queryKey: ['summary'] }, s => s && (isSuggestion({ kind: a.kind, source: a.source ?? '' })
      ? { ...s, contextSuggestions: Math.max(0, s.contextSuggestions - 1) }
      : { ...s, waitingActions: Math.max(0, s.waitingActions - 1) }));
  }
  return snap;
}

const restore = (qc: ReturnType<typeof useQueryClient>, snap: Snapshot) => { for (const [k, v] of snap) qc.setQueryData(k, v); };

/** Every place a card shows (lists, counts, chat, notifications, Today's Needs you). */
const CARDS = ['actions', 'summary', 'notifications', 'conversation', 'today'];
const ACTIVITIES = ['activity', 'activity-files', 'track', 'track-acts', 'track-overview', 'track-updates', 'space', 'space-acts', 'open-activities', 'palette-acts', 'calendar', 'brief'];
/**
 * What deciding a card can change, by its kind: refetched (when on screen) instead of every query in the app, which
 * made each approval refetch and re-render everything.
 */
export function touchedBy(kind: ProposedAction['kind'], approved: boolean): string[] {
  // An email waiting for a place is also listed in Settings, Email logging.
  if (!approved) return kind === 'nudge_check' ? [...CARDS, 'checks', 'check'] : kind === 'log_email' ? [...CARDS, 'email-status'] : CARDS;
  switch (kind) {
    case 'create_activity': case 'update_activity': case 'send_message': return [...CARDS, ...ACTIVITIES];
    case 'file_minutes': return [...CARDS, ...ACTIVITIES, 'meetings', 'meeting', 'reminders'];
    case 'add_file': return [...CARDS, ...ACTIVITIES, 'folders', 'folder', 'file', 'track-files', 'kb-search'];
    case 'create_reminder': return [...CARDS, 'reminders', 'calendar'];
    case 'update_context': return [...CARDS, 'context', 'context-history', 'brief'];
    case 'confirm_answer': case 'review_answer': case 'nudge_check': case 'check_in': return [...CARDS, 'checks', 'check', ...ACTIVITIES];
  }
  return [...CARDS, ...ACTIVITIES];
}

/**
 * A card changed on the server (its next version: changed by typing, moved, a part left out): put it in every list
 * that shows it at once, so Approve sends the version on screen, then refetch what shows cards. Waiting for the
 * refetch left a moment where Approve sent the old version and was refused as stale.
 */
export function useCardChange<A>(fn: (a: A) => Promise<ProposedAction>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: card => {
      const put = (x: ProposedAction) => (x.id === card.id ? card : x);
      qc.setQueriesData<ProposedAction[]>({ queryKey: ['actions'] }, list => list?.map(put));
      qc.setQueriesData<ConversationDetail>({ queryKey: ['conversation'] }, c => (c ? { ...c, actions: c.actions.map(put) } : c));
      return Promise.all(CARDS.map(k => qc.invalidateQueries({ queryKey: [k] })));
    },
  });
}

/** source: when known (a notification row does not carry it; suggestions never have notification rows). */
export interface Decidable { id: string; kind: ProposedAction['kind']; status: ActionStatus; version: number; title: string; source?: ProposedAction['source'] }

/** approve(a) and ignore(a), optimistic with rollback; pending(id) says whether that card is being decided. */
export function useDecide() {
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const settle = (kind: ProposedAction['kind'], approved: boolean) => Promise.all(touchedBy(kind, approved).map(k => qc.invalidateQueries({ queryKey: [k] })));

  const approve = useMutation({
    mutationFn: (a: Decidable) => api.actions.approve(a.id, a.version),
    onMutate: a => ({ snap: applyStatus(qc, a, 'approved') }),
    onError: (e, _a, ctx) => { if (ctx) restore(qc, ctx.snap); toast(`Not approved. ${e instanceof Error ? e.message : ''}`.trim()); },
    onSuccess: res => {
      const link = typeof res.result?.link === 'string' ? res.result.link : null;
      toast(`Approved: ${res.title}`, link ? { label: 'Open', run: () => nav(link) } : undefined);
    },
    onSettled: (_r, _e, a) => settle(a.kind, true),
  });
  const ignore = useMutation({
    mutationFn: (a: Decidable) => api.actions.ignore(a.id),
    onMutate: a => ({ snap: applyStatus(qc, a, 'ignored') }),
    onError: (e, _a, ctx) => { if (ctx) restore(qc, ctx.snap); toast(`Not ignored. ${e instanceof Error ? e.message : ''}`.trim()); },
    onSuccess: res => toast(res.kind === 'log_email' ? 'Dismissed. Nothing was logged.' : 'Ignored. Nothing was done.', res.kind === 'nudge_check' ? undefined : {
      label: 'Undo', run: () => { void api.actions.restore(res.id).then(() => settle(res.kind, false), (e: unknown) => toast(e instanceof Error ? e.message : 'Could not undo.')); },
    }),
    onSettled: (_r, _e, a) => settle(a.kind, false),
  });
  // The click paints first: the card shows it is being decided (a cheap render of this card alone), and the
  // optimistic update of every list that shows it runs right after that frame, not inside it.
  const [clicked, setClicked] = useState<string | null>(null);
  const afterPaint = (fn: () => void) => requestAnimationFrame(() => { setTimeout(fn, 0); });
  const run = (m: typeof approve | typeof ignore, a: Decidable) => {
    setClicked(a.id);
    afterPaint(() => m.mutate(a, { onSettled: () => setClicked(c => (c === a.id ? null : c)) }));
  };
  const busy = (id: string) => clicked === id || (approve.isPending && approve.variables?.id === id) || (ignore.isPending && ignore.variables?.id === id);
  return { approve: (a: Decidable) => run(approve, a), ignore: (a: Decidable) => run(ignore, a), busy };
}
